/// <reference types="vite/client" />

import { createClient } from '@supabase/supabase-js';
import { Database } from './types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validasi variabel lingkungan dengan pesan kesalahan lebih informatif
if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL tidak dijumpai dalam fail .env');
}

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY tidak dijumpai dalam fail .env');
}

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce', // Lebih selamat daripada implicit flow
    },
    global: {
      headers: {
        'x-application-name': 'codedrop-web',
      },
    },
  }
);

// Fungsi pembantu untuk pengendalian ralat Supabase
export const handleSupabaseError = (error: any) => {
  // Log terperinci untuk pemantauan
  const errorDetails = {
    code: error?.code || 'unknown',
    message: error?.message || 'Ralat tidak diketahui',
    timestamp: new Date().toISOString(),
    details: error?.details || {},
    hint: error?.hint || '',
    status: error?.status || null
  };
  
  console.error('Ralat Supabase:', errorDetails);
  
  const errorMessage = error?.message || 'Ralat tidak diketahui telah berlaku';
  const errorCode = error?.code || 'unknown';
  
  // Pengendalian ralat mengikut kategori
  switch (errorCode) {
    // Ralat Pengesahan
    case 'auth/email-already-in-use':
      return 'Alamat e-mel ini telah digunakan oleh akaun lain.';
    case 'auth/invalid-email':
      return 'Alamat e-mel tidak sah. Sila semak format e-mel anda.';
    case 'auth/weak-password':
      return 'Kata laluan terlalu lemah. Sila gunakan kata laluan yang lebih kukuh.';
    case 'auth/user-not-found':
      return 'Pengguna tidak dijumpai. Sila semak e-mel anda atau daftar akaun baharu.';
    case 'auth/wrong-password':
      return 'Kata laluan salah. Sila cuba lagi.';
    
    // Ralat Pangkalan Data
    case 'PGRST301':
      return 'Ralat pangkalan data: Data tidak dijumpai.';
    case 'PGRST302':
      return 'Ralat pangkalan data: Konflik data.';
    
    // Ralat Rangkaian
    case 'network_error':
      return 'Ralat rangkaian. Sila semak sambungan internet anda.';
    
    // Ralat Kebenaran
    case 'auth/insufficient-permissions':
      return 'Anda tidak mempunyai kebenaran untuk melakukan tindakan ini.';
    
    // Ralat Am
    default:
      // Log ralat yang tidak dikenali untuk pemantauan
      console.warn('Ralat tidak dikenali:', errorDetails);
      return errorMessage;
  }
};

// Fungsi untuk menjejak prestasi
export const trackPerformance = (operation: string, startTime: number) => {
  const duration = Date.now() - startTime;
  console.info(`Prestasi ${operation}: ${duration}ms`);
  
  // Di sini boleh tambah logik untuk menghantar metrik ke sistem pemantauan
  if (duration > 1000) {
    console.warn(`Operasi ${operation} mengambil masa lebih dari 1 saat`);
  }
};

// Fungsi untuk menyemak kesihatan sambungan
export const checkConnection = async () => {
  try {
    const start = Date.now();
    const { data, error } = await supabase.from('health_check').select('count').single();
    
    if (error) throw error;
    
    trackPerformance('health_check', start);
    return { status: 'connected', latency: Date.now() - start };
  } catch (error) {
    console.error('Ralat sambungan:', error);
    return { status: 'disconnected', error };
  }
};